import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { UserPermission, UserPermissions } from '../models/permission.model';

@Injectable({
    providedIn: 'root'
})
export class PermissionService {
    private apiUrl = `${environment.apiConfig.uri}/api/Config`;
    private permissionsSubject = new BehaviorSubject<UserPermissions>([]);
    public permissions$ = this.permissionsSubject.asObservable();

    constructor(private http: HttpClient) {
        // Try to load permissions from sessionStorage on service initialization
        this.loadPermissionsFromStorage();
    }

    /**
     * Fetch user permissions from API and store them securely
     * This should be called right after successful login
     */
    fetchUserPermissions(): Observable<UserPermissions> {
        return this.http.get<UserPermissions>(`${this.apiUrl}/user-permissions`).pipe(
            tap(permissions => {
                this.setPermissions(permissions);
            }),
            catchError(error => {
                console.error('Failed to fetch user permissions:', error);
                // Return empty permissions on error
                this.setPermissions([]);
                return of([]);
            })
        );
    }

    /**
     * Store permissions in memory and sessionStorage
     * sessionStorage is cleared when browser tab is closed, providing security
     */
    private setPermissions(permissions: UserPermissions): void {
        this.permissionsSubject.next(permissions);
        // Store in sessionStorage (cleared when browser tab closes)
        sessionStorage.setItem('user_permissions', JSON.stringify(permissions));
    }

    /**
     * Load permissions from sessionStorage on service initialization
     */
    private loadPermissionsFromStorage(): void {
        const storedPermissions = sessionStorage.getItem('user_permissions');
        if (storedPermissions) {
            try {
                const permissions = JSON.parse(storedPermissions) as UserPermissions;
                this.permissionsSubject.next(permissions);
            } catch (error) {
                console.error('Failed to parse stored permissions:', error);
                this.clearPermissions();
            }
        }
    }

    /**
     * Get current permissions synchronously
     */
    getPermissions(): UserPermissions {
        return this.permissionsSubject.value;
    }

    /**
     * Check if user has a specific permission
     */
    hasPermission(permission: UserPermission | string): boolean {
        const permissions = this.getPermissions();
        return permissions.includes(permission);
    }

    /**
     * Check if user has any of the specified permissions
     */
    hasAnyPermission(permissions: (UserPermission | string)[]): boolean {
        return permissions.some(permission => this.hasPermission(permission));
    }

    /**
     * Check if user has all of the specified permissions
     */
    hasAllPermissions(permissions: (UserPermission | string)[]): boolean {
        return permissions.every(permission => this.hasPermission(permission));
    }

    /**
     * Clear permissions (e.g., on logout)
     */
    clearPermissions(): void {
        this.permissionsSubject.next([]);
        sessionStorage.removeItem('user_permissions');
    }

    /**
     * Check if permissions are loaded
     */
    arePermissionsLoaded(): boolean {
        return this.getPermissions().length > 0;
    }
}
